import { KeyValue } from '@angular/common';
import { Component, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Base64Service, Batch, BatchRepositoryService, BatchStateRepositoryService, Bundle, BundlesRepositoryService, ConfigurationState, UploadedDocument, BatchStateForUpdate, eBatchState, BatchDocumentsRepositoryService, DocumentBinary, BatchForUpdate, BatchValidation, eBatchValidationType, ConfirmDialogComponent, UserRepositoryService, OrderMetaData, OrderMetaDataRepositoryService, eBatchType } from 'reg-hub-common';
import { interval, Subscription, switchMap, takeWhile } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'app-draft-batch',
    templateUrl: './draft-batch.component.html',
    styleUrl: './draft-batch.component.css',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DraftBatchComponent implements OnInit {
  private readonly transloco = inject(TranslocoService);
  isSaving: boolean = false;
  isNormalizing: boolean = false;
  bundleID!: string;
  bundle!: Bundle;
  batchID!: string;
  batch!: Batch;
  attachmentErrors: BatchValidation[] = [];
  headerErrors: BatchValidation[] = [];
  dataErrors: BatchValidation[] = [];
  requestingUser: string = "";
  protected _document: UploadedDocument | null = null;
  private pollingSubscription!: Subscription;
  private submissionText: KeyValue<eBatchType,string>[] = [
    { key: eBatchType.lien, value: 'bundles.submitConfirm.lien' },
    { key: eBatchType.historicalLien, value: 'bundles.submitConfirm.historicalLien' },
    { key: eBatchType.renewal, value: 'bundles.submitConfirm.renewal' },
    { key: eBatchType.basicRenewal, value: 'bundles.submitConfirm.renewal' },
    { key: eBatchType.discharge, value: 'bundles.submitConfirm.discharge' },
    { key: eBatchType.basicDischarge, value: 'bundles.submitConfirm.discharge' },
    { key: eBatchType.assetSearch, value: 'bundles.submitConfirm.assetSearch' },
    { key: eBatchType.debtorSearch, value: 'bundles.submitConfirm.debtorSearch' },
    { key: eBatchType.caBusinessSearch, value: 'bundles.submitConfirm.caBusinessSearch' },
    { key: eBatchType.caBusinessReport, value: 'bundles.submitConfirm.caBusinessReport' },
    { key: eBatchType.usBusinessSearch, value: 'bundles.submitConfirm.usBusinessSearch' },
    { key: eBatchType.usBusinessReport, value: 'bundles.submitConfirm.usBusinessReport' },
    { key: eBatchType.registrationNumberSearch, value: 'bundles.submitConfirm.registrationNumberSearch' },
    { key: eBatchType.ultimateBeneficialOwnershipReport, value: 'bundles.submitConfirm.ultimateBeneficialOwnershipReport' },
  ];

  constructor(
    private batchesService: BatchRepositoryService,
    private batchDocumentsService: BatchDocumentsRepositoryService,
    private batchStatesService: BatchStateRepositoryService,
    private bundlesService: BundlesRepositoryService,
    private configurationState: ConfigurationState,
    private orderMetaDataService: OrderMetaDataRepositoryService,
    private dialog: MatDialog,
    private messageService: ToastrService,
    private base64Service: Base64Service,
    private router: Router,
    private activatedRoute: ActivatedRoute) { }

  ngOnInit(): void {
    this.activatedRoute.paramMap.subscribe(params => {
      this.bundleID = params.get('bundleID')!;
      this.batchID = params.get('batchID')!;
      this.getBatch();
      this.getBundle();
    });
  }

  ngOnDestroy() {
    this.stopPolling();
  }

  getBundle() {
    this.configurationState.getCorporationID().then(corporationID => {
      this.bundlesService.populateBaseLink([corporationID]);
      this.bundlesService.getBundle(this.bundleID).subscribe(bundle => {
        this.bundle = bundle;
      })
    })
  }

  getBatch() {
    this.configurationState.getCorporationID().then(corporationID => {
      this.batchesService.populateBaseLink([corporationID]);
      this.batchesService.getBatch(this.bundleID, this.batchID).subscribe(batch => {
        this.batch = batch;
        if (batch.batchValidations) {
          this.attachmentErrors = batch.batchValidations.filter(error => error.batchValidationTypeID === eBatchValidationType.attachment);
          this.headerErrors = batch.batchValidations.filter(error => error.batchValidationTypeID === eBatchValidationType.headers);
          this.dataErrors = batch.batchValidations.filter(error => error.batchValidationTypeID === eBatchValidationType.data);
        }
        this.getRequestingUser(batch);
      })
    });
  }

  getRequestingUser(batch: Batch) {
    this.orderMetaDataService
      .getOrderMetaData(batch.corporationID, null, batch.userID, null).subscribe(data => {
        this.requestingUser = data.requestingUserFullName ?? "";
    })
  }

  downloadSample() {
    this.batchesService.getBatchSample(this.batch.batchTypeID)
      .subscribe(result => {
        const blobURL = URL.createObjectURL(result);
        const link = document.createElement("a");
        link.href = blobURL;
        link.download = `${this.batch.batchType.name.replaceAll(" ","_")}_Sample.csv`; // set a name for the file
        link.click();
      });
  }

  downloadReadme() {
    this.batchesService.getBatchReadme(this.batch.batchTypeID)
      .subscribe(result => {
        const blobURL = URL.createObjectURL(result);
        const link = document.createElement("a");
        link.href = blobURL;
        link.download = `${this.batch.batchType.name.replaceAll(" ","_")}_Readme.txt`; // set a name for the file
        link.click();
      });
  }

  protected onFileSelected(event: Event) {
    const element = event.currentTarget as HTMLInputElement;
    let file = element.files ? element.files[0] : null;

    if (file !== null) {
      this.base64Service.convertToBase64(file)
        .subscribe(base64 => {
          this._document = {
            binary: base64,
            file: file
          };
        });
    }
  }

  protected onDeleteDocument() {
    this._document = null;
  }

  submitBatchConfirmation() {
    var submissionText = this.getSubmissionText(this.batch.batchTypeID);
    const dialogRef =this.dialog.open(ConfirmDialogComponent, {
      data: {
          title: this.transloco.translate('bundles.submitBatch'),
          confirmationPrompt: submissionText,
          positiveResponseLabel: this.transloco.translate('common.buttons.submit'),
          negativeResponseLabel: this.transloco.translate('common.buttons.cancel')
      },
      minWidth: '800px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // submission is confirmed, submit batch
        this.saveBatch();
      }
    });
  }

  saveBatch() {
    this.isSaving = true;
    //Upload Document
    if (this._document?.file) {
      let document: DocumentBinary = {
        fileName: this._document?.file!.name!,
        documentTypeID: this.getDocumentType(this._document!.file!),
        base64: this._document!.binary,
      }
      this.batchDocumentsService.createDocumentBinary(document).subscribe(document => {
        this.updateBatch(document.id);
      })
    }
    else {
      this.updateBatch(this._document!.document!.id);
    }
  }

  updateBatch(documentID: string) {
    let batchForUpdate = this.batch as BatchForUpdate;
    batchForUpdate.rawDocumentID = documentID;
    this.batchesService.updateBatch(this.bundleID, this.batchID, batchForUpdate).subscribe({
      next: () => {
        this.submitBatch();
      },
      error: err => {
        console.error(err);
        this.messageService.error(this.transloco.translate('bundles.failedToSaveBatch'));
      }
    })
  }

  downloadBatch() {
    
  }

  deleteBatchConfirmation() {
    const dialogRef =this.dialog.open(ConfirmDialogComponent, {
      data: {
          title: this.transloco.translate('bundles.deleteBatch'),
          confirmationPrompt: this.transloco.translate('bundles.confirmPermanentlyDeleteThisDraftBatch'),
          positiveResponseLabel: this.transloco.translate('common.buttons.delete'),
          negativeResponseLabel: this.transloco.translate('common.buttons.cancel')
      },
      minWidth: '800px'
  });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // submission is confirmed, submit order
        this.deleteBatch();
      }
    });
  }

  deleteBatch() {
    this.batchesService.deleteBatch(this.bundleID, this.batchID)
      .subscribe(_ => {
        this.router.navigate(['bundles',this.bundleID]);
      });
  }

  getDocumentType(file: File): string {
    switch (file.type) {
      case 'application/pdf':
        return 'PDF';
      case 'text/plain':
        return 'Text';
      case 'application/xml':
      case 'text/xml':
        return 'XML';
      case 'text/html':
        return 'HTML';
      case 'application/json':
        return 'JSON';
      case 'text/csv':
      case 'application/vnd.ms-excel': // Some browsers use this for CSV
        return 'CSV';
      default:
        return 'PDF';
    }
  }

  submitBatch() {
    //Change Batch State to Normlization
    let newState: BatchStateForUpdate = {
      newState: eBatchState.Normalization
    };
    this.batchStatesService.updateBatchState(this.bundleID, this.batchID, newState).subscribe({
      next: () => {
        this.isSaving = false;
        this.isNormalizing = true;
        this.pollBatch();
      },
      error: err => {
        console.error(err);
        this.messageService.error(this.transloco.translate('bundles.failedToSaveBatch'));
      }
    })
  }

  pollBatch() {
    this.pollingSubscription = interval(1000)
    .pipe(switchMap(() => this.batchesService.getBatch(this.bundleID, this.batchID)),
    takeWhile(batch => batch.batchStateID == eBatchState.Normalization, true))
    .subscribe(batch => {
      this.batch = batch;
      if (this.batch.batchStateID == eBatchState.draft) {
        this.isNormalizing = false;
        if (batch.batchValidations) {
          this.attachmentErrors = batch.batchValidations.filter(error => error.batchValidationTypeID === eBatchValidationType.attachment);
          this.headerErrors = batch.batchValidations.filter(error => error.batchValidationTypeID === eBatchValidationType.headers);
          this.dataErrors = batch.batchValidations.filter(error => error.batchValidationTypeID === eBatchValidationType.data);
        }
      }
      else if (this.batch.batchStateID != eBatchState.Normalization) {
        this.router.navigate(['bundles', this.bundleID, 'batches', this.batchID]);
        this.stopPolling();
      }
    });
  }

  stopPolling() {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
  }

  getSubmissionText(batchType: eBatchType): string {
    return this.transloco.translate(this.submissionText.find(text => text.key == batchType)?.value ?? 'bundles.submitConfirm.generic', { batchType });
  }
}
